import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  ColDef,
  GridOptions,
  GridReadyEvent,
  IGetRowsParams,
  ValueFormatterParams
} from 'ag-grid-community';
import { StoreListInterface } from '@/model/storeList.mjs';
import {
  Box,
  Button,
  Heading,
  Link,
  Menu,
  MenuButton,
  MenuList,
  Text,
  Tooltip,
  useDisclosure,
  Spinner,
  Switch,
  HStack
} from '@chakra-ui/react';
import { agGridIconsConfig } from '@/explore/ffl-detail/utils.mjs';
import StoresDataSource from '../ducks/storeDataSource';
import { dateFormatter } from '@/queue/elements/value-formatters/date-formatter.mjs';
import { getStoreRefresh } from '@/redux/app-selectors';
import { useDispatch } from 'react-redux';
import { callToImpersonateGlobalAdmin, setStoreRefresh } from '@/redux/app-slice';
import { useNavigate } from 'react-router';
import { IsUUID } from '@/utils/utils';
import { getStoresFilterModel } from './selector';
import { putStoreDetails } from '@/api/apis';
import ConfirmationModal from '@/common/ConfirmationModal';
import { getIsGlobalAdmin } from '@/redux/app-selectors';
import StoreDetailsInterface from '@/model/store.mjs';
import { ToasterNotification } from '@/toaster/toasterNotification';

interface props {
  doRefresh: number;
}

function RenderAction(data: any) {
  const navigate = useNavigate()
  const dispatch = useDispatch();
  const isGlobalAdmin = getIsGlobalAdmin();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const onclickImpersonate = () => {
    dispatch(callToImpersonateGlobalAdmin({storeId: data.data.store_id, navigate}))
  }

  const handleDeleteClick = () => {
    setIsDeleteModalOpen(true);
  }

  const handleDeleteConfirm = async () => {
    try {
      setIsDeleting(true);
      const payload = {
        is_deleted: true,
        store_id: data.data.store_id
      }
      await putStoreDetails(payload as StoreDetailsInterface);
      setIsDeleteModalOpen(false);
      // Show success toast
      ToasterNotification('success', 'Store deleted successfully');
      // Refresh the grid data
      if (data.context?.gridRef?.current?.api) {
        data.context.gridRef.current.api.refreshServerSide();
      }
    } catch (error) {
      console.error('Error deleting store:', error);
      ToasterNotification('error', 'Failed to delete store. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  }

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
  }
  
  return (
    <>
      <div className="actions-columm">
        <button className="actions-column__btn" onClick={onclickImpersonate} title='Impersonate Store'>
          <img src="/images/ui/impersonate.svg"></img>
        </button>
        {isGlobalAdmin && !data.data.is_external && (
          <button 
            className="actions-column__btn" 
            onClick={handleDeleteClick} 
            title='Delete Store'
            style={{ marginLeft: '8px' }}
            disabled={isDeleting}
          >
            <img 
              src="/images/ui/delete.svg" 
              alt="Delete"
              style={{ width: '16px', height: '16px' }}
            />
          </button>
        )}
      </div>
      
              <ConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={handleDeleteCancel}
          modalHeader="Delete Store"
          isCancelBtnDisable={false}
          isConfirmBtnDisable={false}
          isConfirmBtnLoading={isDeleting}
          onClickConfirmBtn={handleDeleteConfirm}
          modalContent={
            <Box>
              <Text>Are you sure you want to delete the store "{data.data.store_name}"?</Text>
              <Text mt={2} fontSize="sm" color="gray.600">
                This will soft delete the store, and all associated transfers will no longer be visible. Please note that this action is irreversible.
              </Text>
            </Box>
          }
        />
    </>
  );
}

const Stores = ({ doRefresh }: props) => {
  const storeRefresh = getStoreRefresh();
  const dispatch = useDispatch();
  const gridRef = useRef<AgGridReact<StoreListInterface>>(null);
  const gridStyle = useMemo(() => ({ width: '100%' }), []);
  const [fetchTransferCounts, setFetchTransferCounts] = useState(false);
  const storeDataSource = useMemo(() => new StoresDataSource(fetchTransferCounts), [fetchTransferCounts]);
  const adminStoreFilterModel = getStoresFilterModel();

  useEffect(() => {
    if (doRefresh === 0 || storeRefresh) reFetchData();
  }, [doRefresh, storeRefresh]);

  useEffect(() => {
    if (gridRef.current && gridRef.current?.api) {
      gridRef.current.api.setServerSideDatasource(storeDataSource);

      const transferColumns = [
        'total_transfers',
        'transfers_month',
        'nfa_transfers',
        'nfa_monthly_transfers',
        'avg_daily_transfers'
      ];

      gridRef.current.api.setColumnsVisible(transferColumns, fetchTransferCounts);
    }
  }, [storeDataSource, fetchTransferCounts]);

  
  useEffect(() => {
    if(gridRef.current && gridRef.current?.api){
      const values = adminStoreFilterModel?.is_external?.values.map((item: any) => item === "live" ? 'true' : 'false');
      gridRef.current?.api?.setFilterModel({
        "is_external": {
          filterType: "set",
          values: values
        },
      });
    }
  }, [adminStoreFilterModel, gridRef.current?.api])
  

  function reFetchData() {
    gridRef.current?.api?.refreshServerSide();
    dispatch(setStoreRefresh(false));
  }

  function renderStoreDomain({ value, data }: { value: string, data: any }) {
    return <Box sx={{display: 'flex', flexDirection: 'row'}}>
      {value && IsUUID(value) ? '' : value || ""}
      {data?.store_domains && Array.isArray(data.store_domains) && data.store_domains.length > 1 && (
        <Tooltip
          label={
            <Box>
              <span>
                {data.store_domains.slice(1).map((domainVal: string, index: number) => (
                  <Text key={`domain_${index}`}>{domainVal}</Text>
                ))}
              </span>
            </Box>
          }
          hasArrow
          placement="bottom"
          color={'black'}
          bg={'white'}
        >
          <Text sx={{ marginLeft: '1rem' }}>
            +{data.store_domains.length - 1}
          </Text>
        </Tooltip>
      )}
    </Box>
  }

  function renderHyperLink(data: any) {
    return (
      <Link href={`mailto:${data.data.email}`} isExternal color="#0065FF">
        {data.data.store_admin}
      </Link>
    );
  }
  function dateFormatting({ value }: { value: number }) {
    const date = new Date(value * 1000);
    const formattedDate = date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    return <>{value ? formattedDate : null}</>;
  }

  function convertStoreType({ value }: { value: any }) {
      return <>{(value || value === 'true') ? "Live" : "Testing"}</>
  }

  function valueFormat({ value }: { value: number }) {
    return <>{value ? value.toLocaleString('en-US') : "0"}</>;
  }

  const columnDefs: ColDef[] = [
    {
      field: 'store_name',
      headerName: 'Store Name',
      width: 250
    },
    {
      headerName: 'Actions',
      cellRenderer: RenderAction,
      cellRendererParams: {
        context: { gridRef }
      },
      width: 200
    },
    {
      field: 'store_id',
      headerName: 'Store Id',
      width: 200,
      cellRenderer: valueFormat
    },
    {
      field: 'store_domain',
      headerName: 'Store Domain',
      cellRenderer: renderStoreDomain,
      width: 200
    },
    {
      field: 'store_admin',
      headerName: 'Store Admin',
      cellRenderer: renderHyperLink,
      width: 200
    },
    {
      field: 'agents',
      headerName: 'Users',
      width: 200
    },
    {
      field: 'total_transfers',
      headerName: 'Total Transfers',
      width: 200,
      cellRenderer: valueFormat
    },
    {
      field: 'transfers_month',
      headerName: 'Transfers this Month',
      width: 250,
      cellRenderer: valueFormat
    },
    {
      field: 'nfa_transfers',
      headerName: 'NFA Transfers',
      width: 200,
      cellRenderer: valueFormat
    },
    {
      field: 'nfa_monthly_transfers',
      headerName: 'NFA Transfers this Month',
      width: 280,
      cellRenderer: valueFormat
    },
    {
      field: 'avg_daily_transfers',
      headerName: 'Avg. Daily Transfers',
      width: 230,
      cellRenderer: valueFormat
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 150
    },
    {
      field: 'customer_since',
      headerName: 'Customer Since',
      width: 200,
      cellRenderer: dateFormatting
    },
    {
        field: 'is_external',
        headerName: 'Store Type',
        width: 200,
        filter: "agSetColumnFilter",
        filterParams: {
          values: [true, false]
        },
        cellRenderer: convertStoreType
    },
    {
      field: 'module_version',
      headerName: 'FFL Checkout Version',
      width: 250
    },
  ];

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: true
    }),
    []
  );

  const gridOptions: GridOptions<StoreListInterface> = {
    rowModelType: 'serverSide',
    serverSideDatasource: storeDataSource,
    pagination: false,

    // generate a unique ID for each row based on the abuse ID
    getRowId: (params) => {
      return params.data?.store_id || '';
    },

    // column definitions
    columnDefs,
    defaultColDef,

    // row options
    animateRows: true,
    suppressRowClickSelection: true,

    // cell detail options
    suppressCellFocus: true,

    // misc options
    debug: false,
    onGridReady(params) {
      const values = adminStoreFilterModel?.is_external?.values.map((item: any) => item === "live"? 'true' : 'false');

      params.api.setFilterModel({
        "is_external": {
          filterType: 'set',
          values: values
        }
      });

      const transferColumns = [
        'total_transfers',
        'transfers_month',
        'nfa_transfers',
        'nfa_monthly_transfers',
        'avg_daily_transfers'
      ];
      params.api.setColumnsVisible(transferColumns, false);

      params.api.addEventListener('modelUpdated', () => {
        const rowCount = gridRef?.current?.api.getDisplayedRowCount();
        if(rowCount) gridRef.current?.api.hideOverlay();
        else gridRef.current?.api.showNoRowsOverlay();
      })
    },
    icons: agGridIconsConfig,
    overlayNoRowsTemplate:
      '<div class="not-found-box"><img src="/images/ui/not-found-icon.png"></img><p class="not-found-box__title">No Store Found</p><p class="not-found-box__message">Try adjusting your filter to find what you\'re looking for</p></div>'
  };

  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api) return;

    const checkDataAvailability = () => {
      const rowCount = api.getDisplayedRowCount();
      if (rowCount) api.hideOverlay();
      else api.showNoRowsOverlay();
    };

    checkDataAvailability();
    api.addEventListener('modelUpdated', checkDataAvailability);

    return () => {
      api.removeEventListener('modelUpdated', checkDataAvailability);
    };
  }, [gridRef?.current?.api]);

  return (
    <>
      <HStack justify="space-between" align="center" paddingBottom={'2rem'}>
        <Heading size={'md'}>
          Stores
        </Heading>
        <HStack spacing={2}>
          <Text fontSize="sm">Load Transfer Counts</Text>
          <Switch
            isChecked={fetchTransferCounts}
            onChange={(e) => setFetchTransferCounts(e.target.checked)}
          />
        </HStack>
      </HStack>
      <div className="store-grid-container" style={gridStyle}>
        <AgGridReact
          className={'ag-theme-alpine'}
          ref={gridRef}
          gridOptions={gridOptions}
        />
      </div>
    </>
  );
};

export default Stores;
